import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  Bell,
  CalendarClock,
  FileText,
  Home,
  Search,
  MessageCircle,
  Wrench,
} from "lucide-react";
import { getMyProfile } from "@/lib/profile.functions";
import { listHomes } from "@/lib/homes.functions";
import { getCustomerAttention, listMyBookings } from "@/lib/bookings.functions";
import { bookingStatusLabel, ukDate } from "@/lib/format";
import { AppHeader } from "@/components/site/AppHeader";
import { PageShell, SectionHeader } from "@/components/ui/shields-layout";

const profileQuery = queryOptions({
  queryKey: ["my-profile"],
  queryFn: () => getMyProfile(),
});

const homesQuery = queryOptions({
  queryKey: ["homes"],
  queryFn: () => listHomes(),
});

const bookingsQuery = queryOptions({
  queryKey: ["my-bookings"],
  queryFn: () => listMyBookings(),
});

const attentionQuery = queryOptions({
  queryKey: ["customer-attention"],
  queryFn: () => getCustomerAttention(),
});

export const Route = createFileRoute("/_authenticated/customer/")({
  head: () => ({
    meta: [
      { title: "Home — Shields" },
      {
        name: "description",
        content: "Book trusted local services and manage your home in one place.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: CustomerHome,
  pendingComponent: () => <DashboardLoading label="Loading your Shields home…" />,
  errorComponent: () => <DashboardError label="We couldn’t load your Shields home." />,
});

const quickActions = [
  {
    to: "/book" as const,
    icon: Wrench,
    title: "Book a Service",
    detail: "Find trusted professionals",
    tone: "gold",
  },
  {
    to: "/home" as const,
    icon: Home,
    title: "My Home",
    detail: "Manage your property",
    tone: "navy",
  },
  {
    to: "/bookings" as const,
    icon: CalendarClock,
    title: "Activity",
    detail: "Jobs, quotes and progress",
    tone: "green",
  },
  {
    to: "/home/vault" as const,
    icon: FileText,
    title: "Documents",
    detail: "Certificates and warranties",
    tone: "purple",
  },
  {
    to: "/home/maintenance" as const,
    icon: Bell,
    title: "Reminders",
    detail: "Keep on top of maintenance",
    tone: "navy",
  },
  {
    to: "/messages" as const,
    icon: MessageCircle,
    title: "Messages",
    detail: "Chat about your active jobs",
    tone: "blue",
  },
] as const;

function CustomerHome() {
  const { data } = useSuspenseQuery(profileQuery);
  const { data: homes } = useSuspenseQuery(homesQuery);
  const { data: bookings } = useSuspenseQuery(bookingsQuery);
  const { data: attention } = useSuspenseQuery(attentionQuery);

  const active = bookings.filter(
    (booking) =>
      !["completed", "closed", "cancelled", "declined", "expired"].includes(
        booking.status,
      ),
  );
  const firstName =
    data.profile?.full_name?.trim().split(/\s+/)[0] || "there";
  const primaryProperty = data.properties[0] ?? null;
  const savedHome = homes.find((home) => home.isActive) ?? homes[0] ?? null;
  const nextBooking = [...active]
    .filter((booking) => Boolean(booking.scheduled_date || booking.preferred_date))
    .sort((a, b) =>
      String(a.scheduled_date || a.preferred_date || "").localeCompare(
        String(b.scheduled_date || b.preferred_date || ""),
      ),
    )[0];

  const locationLine = savedHome
    ? [savedHome.addressLine, savedHome.townCity].filter(Boolean).join(", ") || savedHome.label
    : primaryProperty
    ? [primaryProperty.address_line, primaryProperty.town_city]
        .filter(Boolean)
        .join(", ")
    : "Add your home";
  const postcode = savedHome?.postcode ?? primaryProperty?.postcode ?? "Keep your property details together";
  const homeCard = (
    <>
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/12 text-gold">
        <Home className="h-6 w-6" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <strong className="block truncate text-base">{locationLine || "Your home"}</strong>
        <small className="mt-0.5 block truncate text-sm text-white/65">{postcode}</small>
      </span>
      <span className="text-sm font-bold text-gold">Manage</span>
      <ArrowRight className="h-5 w-5 shrink-0" aria-hidden />
    </>
  );

  return (
    <div className="shields-customer-home min-h-screen bg-background pb-24">
      <AppHeader workspace="customer" avatarUrl={data.avatarUrl} />

      <PageShell width="narrow" className="px-0 py-0">
        <section className="shields-customer-hero bg-navy px-4 pb-6 pt-5 text-white sm:rounded-b-[2rem] sm:px-6">
          <div className="shields-customer-hero__intro">
            <p className="shields-customer-hero__eyebrow">SHIELDS HOME <span aria-hidden>✦</span> YOUR HOME, HANDLED</p>
            <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">Hello, {firstName}.</h1>
            <p className="mt-1 text-lg text-white/75">What would you like to get done today?</p>
          </div>

          {savedHome ? (
            <Link to="/home/$homeId" params={{ homeId: savedHome.id }} className="shields-customer-hero__home mt-5 flex min-h-20 items-center gap-3 rounded-2xl border border-white/15 bg-white/5 p-4 transition hover:bg-white/10">
              {homeCard}
            </Link>
          ) : (
            <Link to="/home" className="shields-customer-hero__home mt-5 flex min-h-20 items-center gap-3 rounded-2xl border border-white/15 bg-white/5 p-4 transition hover:bg-white/10">
              {homeCard}
            </Link>
          )}

          <Link
            to="/book"
            className="shields-customer-hero__book mt-4 flex min-h-14 items-center gap-3 rounded-2xl bg-white px-4 text-muted-foreground shadow-sm"
          >
            <Search className="h-6 w-6 shrink-0 text-foreground" aria-hidden />
            <span className="flex-1 text-base">What do you need help with?</span><ArrowRight className="h-5 w-5" aria-hidden />
          </Link>
        </section>

        <div className="px-4 py-5 sm:px-6">
          <nav
            className="shields-customer-actions grid grid-cols-2 gap-3 sm:grid-cols-3"
            aria-label="Customer quick actions"
          >
            {quickActions.map(({ to, icon: Icon, title, detail, tone }) => (
              <Link
                key={title}
                to={to}
                className={"card-surface shields-customer-action group flex min-h-36 flex-col items-center justify-center p-4 text-center" + (tone === "gold" ? " shields-customer-action--featured" : "")} >
                <span className="shields-customer-action__icon"><Icon className="h-6 w-6" aria-hidden /></span>
                <strong className="mt-3 text-base">{title}</strong>
                <small className="mt-1 max-w-[11rem] text-sm leading-5">
                  {detail}
                </small>
              </Link>
            ))}
          </nav>

          <section className="mt-7">
            <SectionHeader title="Needs your attention"
              action={<Link to="/bookings" className="text-sm font-bold text-primary">View all</Link>} />

            {attention.length ? (
              <div className="mt-3 space-y-2">
                {attention.slice(0, 3).map((item) => (
                  <Link
                    key={item.id}
                    to={item.to as never}
                    className="flex min-h-16 items-center gap-3 rounded-2xl border border-border bg-card p-4"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary">
                      {item.kind === "reminder" ? (
                        <Bell className="h-5 w-5" aria-hidden />
                      ) : (
                        <Wrench className="h-5 w-5" aria-hidden />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <strong className="block truncate text-foreground">
                        {item.title}
                      </strong>
                      <small className="block truncate text-sm text-muted-foreground">
                        {item.detail}
                      </small>
                    </span>
                    <ArrowRight className="h-5 w-5 text-muted-foreground" aria-hidden />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="mt-3 rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground">
                You’re all caught up.
              </div>
            )}
          </section>

          <section className="mt-7">
            <SectionHeader title="Upcoming"
              action={<Link to="/bookings" className="text-sm font-bold text-primary">View all</Link>} />

            {nextBooking ? (
              <Link
                to="/bookings/$bookingId"
                params={{ bookingId: nextBooking.id }}
                className="mt-3 flex min-h-20 items-center gap-3 rounded-2xl border border-border bg-card p-4"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary">
                  <CalendarClock className="h-5 w-5" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <strong className="block truncate text-foreground">
                    {(nextBooking.services as { name?: string } | null)?.name ??
                      "Service"}
                  </strong>
                  <small className="block truncate text-sm text-muted-foreground">
                    {nextBooking.scheduled_date
                      ? ukDate(nextBooking.scheduled_date)
                      : nextBooking.preferred_date
                        ? ukDate(nextBooking.preferred_date)
                        : bookingStatusLabel(nextBooking.status)}
                    {nextBooking.scheduled_window
                      ? " · " + nextBooking.scheduled_window
                      : ""}
                  </small>
                </span>
                <ArrowRight className="h-5 w-5 text-muted-foreground" aria-hidden />
              </Link>
            ) : (
              <Link
                to="/book"
                className="mt-3 flex min-h-20 items-center gap-3 rounded-2xl border border-border bg-card p-4"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold">
                  <Wrench className="h-5 w-5" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <strong className="block text-foreground">Nothing booked yet</strong>
                  <small className="block text-sm text-muted-foreground">
                    Start a service request whenever you need help.
                  </small>
                </span>
                <ArrowRight className="h-5 w-5 text-muted-foreground" aria-hidden />
              </Link>
            )}
          </section>

          <Link
            to="/home"
            className="mt-5 flex min-h-24 items-center gap-4 rounded-2xl border border-gold/35 bg-gold/5 p-4"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold">
              <Home className="h-6 w-6" aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <strong className="block text-lg text-foreground">Look after your home</strong>
              <small className="mt-1 block text-sm leading-5 text-muted-foreground">
                Service history, documents, reminders, previous Partners and household sharing.
              </small>
            </span>
            <ArrowRight className="h-5 w-5 text-muted-foreground" aria-hidden />
          </Link>
        </div>
      </PageShell>
    </div>
  );
}

function DashboardLoading({ label }: { label: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6 text-sm text-muted-foreground">
      {label}
    </div>
  );
}

function DashboardError({ label }: { label: string }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-6 text-center">
      <Home className="h-8 w-8 text-primary" />
      <strong className="mt-3">{label}</strong>
      <Link to="/account" className="btn-hero mt-4">
        Open account
      </Link>
    </div>
  );
}
